import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import VuexPersistence from 'vuex-persist';
import getUrl from '../Helpers/BaseUrl';
import { Todos } from './Modules/Todos/Todos';
import { Appointments } from './Modules/Appointments/Appointments';
import { Relations } from './Modules/Relations/Relations';
import { Employees } from './Modules/Employees/Employees';
import { navigateToLogin } from '@/Helpers/NavigationHelpers';
import { datadogRum } from '@datadog/browser-rum';
import { trackAction } from './Helpers/UserMonitor';
Vue.use(Vuex);
const vuexLocal = new VuexPersistence({
    key: 'CRMWeb',
    storage: localStorage,
    reducer: (state) => ({
        loginData: state.loginData,
        userName: state.userName,
        permissions: state.permissions
    }),
    filter: (mutation) => ['setLoginData', 'setUserName', 'setPermissionsData'].includes(mutation.type)
});
function createState() {
    return {
        loginData: undefined,
        webserviceHasError: false,
        webserviceError: undefined,
        webserviceStatusCode: 0,
        isLoadingData: false,
        showDrawer: false,
        userName: undefined,
        permissions: undefined,
        lat: 0,
        lon: 0
    };
}
export default new Vuex.Store({
    plugins: [vuexLocal.plugin],
    state: createState(),
    modules: {
        Todos,
        Appointments,
        Relations,
        Employees
    },
    mutations: {
        setLocation(state, payload) {
            console.log(`Got new position - Lat: ${payload.lat} lon: ${payload.lon}`);
            state.lat = payload.lat;
            state.lon = payload.lon;
        },
        setUserName(state, payload) {
            state.userName = payload;
        },
        setShowDrawer(state, payload) {
            state.showDrawer = payload;
        },
        setLoginData(state, payload) {
            state.loginData = payload;
        },
        setPermissionsData(state, payload) {
            state.permissions = payload;
        },
        setWebserviceError(state, payload) {
            state.webserviceError = payload;
            state.webserviceHasError = true;
        },
        setWebserviceStatusCode(state, payload) {
            state.webserviceStatusCode = payload;
            state.webserviceHasError = true;
        },
        clearWebserviceError(state) {
            state.webserviceError = undefined;
            state.webserviceHasError = false;
            state.webserviceStatusCode = 0;
        },
        setIsLoadingData(state, payload) {
            state.isLoadingData = payload;
        },
        resetState(state) {
            const userName = state.userName;
            Object.assign(state, createState());
            //Restore username so it's auto-filled in login form
            state.userName = userName;
        }
    },
    actions: {
        getLocation(state) {
            console.log('getting location');
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => state.commit('setLocation', { lat: position.coords.latitude, lon: position.coords.longitude }));
            }
            else {
                console.log("Geolocation is not supported by this browser.");
            }
        },
        resetState(state) {
            state.commit('resetState');
            state.commit('Todos/resetState');
            state.commit('Appointments/resetState');
            state.commit('Relations/resetState');
            state.commit('Employees/resetState');
        },
        async login(store, payload) {
            store.commit("setIsLoadingData", true);
            try {
                const response = await axios.post(getUrl(`/api/${process.env.VUE_APP_API_VERSION}/Session/Login`), payload, { _neverRetry: true });
                if (response.data) {
                    if (store.state.loginData?.CompanyName != response.data.CompanyName ||
                        store.state.loginData?.EmployeeId != response.data.EmployeeId) {
                        store.commit('resetState');
                    }
                    store.commit("setLoginData", response.data);
                    let domain = response.data.CompanyName;
                    try {
                        domain = payload.UserName.split('@')[1];
                    }
                    catch {
                        console.error("Username contains no @");
                    }
                    datadogRum.setUser({ id: domain, name: response.data.CompanyName });
                    trackAction("Login");
                    const permissionResponse = await axios.get(getUrl(`/api/CRM/${process.env.VUE_APP_API_VERSION}/Permissions`), { _neverRetry: true });
                    if (permissionResponse.data) {
                        store.commit("setPermissionsData", permissionResponse.data);
                    }
                    else {
                        store.commit("setPermissionsData", undefined);
                    }
                }
            }
            finally {
                store.commit("setIsLoadingData", false);
            }
        },
        async refreshToken(store) {
            store.commit("setIsLoadingData", true);
            const response = await axios.get(getUrl(`/api/${process.env.VUE_APP_API_VERSION}/Session/RefreshToken`), { _neverRetry: true });
            if (response) {
                trackAction("Refresh token");
                store.commit("setLoginData", response.data);
            }
            else {
                trackAction("Refresh token failed");
            }
        },
        async logout(store) {
            try {
                const response = await axios.get(getUrl(`/api/${process.env.VUE_APP_API_VERSION}/Session/Logout`), { _neverRetry: true });
            }
            finally {
                store.commit("setLoginData", null);
                store.dispatch('resetState');
                trackAction("Logout");
                datadogRum.removeUser();
                navigateToLogin();
            }
        }
    },
    getters: {
        IsAuthenticated(state) {
            return state.loginData != null;
        },
        GetCompany(state) {
            if (state.loginData != null)
                return state.loginData.CompanyName;
            return "";
        },
        GetUserName(state) {
            if (state.loginData != null)
                return state.loginData.UserFullName;
            return "";
        },
        GetToken(state) {
            if (state.loginData != null)
                return state.loginData.Token;
            return "";
        },
        GetLoggedInUntill(state) {
            if (state.loginData != null)
                return Date.parse(state.loginData.ValidUntil);
            return new Date();
        },
        userLanguage(state, getters) {
            if (getters.IsAuthenticated && state.loginData?.Language) {
                return state.loginData.Language;
            }
            //fallback to default
            return navigator.language;
        }
    }
});
